import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type FC,
  type JSX,
  type PropsWithChildren,
} from 'react';
import { Prompt } from 'react-router';

type UnsavedChangesRegistry = {
  setDirty: (formId: string, isDirty: boolean) => void;
};

const SettingsUnsavedChangesContext = createContext<
  UnsavedChangesRegistry | undefined
>(undefined);

/**
 * Declares whether this sub-form holds unsaved changes. A settings section can
 * stack several sub-forms, and `history` supports only one navigation prompt at
 * a time, so the forms report up to a single guard rather than each mounting
 * their own `Prompt` and clobbering one another.
 */
export const useReportUnsavedChanges = (isDirty: boolean): void => {
  const registry = useContext(SettingsUnsavedChangesContext);
  const formId = useId();

  useEffect(() => {
    registry?.setDirty(formId, isDirty);
    return () => registry?.setDirty(formId, false);
  }, [registry, formId, isDirty]);
};

/** Warns once before leaving a settings section while any sub-form is dirty. */
export const SettingsUnsavedChangesProvider: FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [dirtyFormIds, setDirtyFormIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const setDirty = useCallback((formId: string, isDirty: boolean) => {
    setDirtyFormIds((prev) => {
      if (prev.has(formId) === isDirty) return prev;
      const next = new Set(prev);
      if (isDirty) {
        next.add(formId);
      } else {
        next.delete(formId);
      }
      return next;
    });
  }, []);

  const registry = useMemo(() => ({ setDirty }), [setDirty]);

  return (
    <SettingsUnsavedChangesContext.Provider value={registry}>
      <Prompt
        when={dirtyFormIds.size > 0}
        message="There are unsaved changes on the page, are you sure you want to leave?"
      />
      {children}
    </SettingsUnsavedChangesContext.Provider>
  );
};
